import {getSiteLocale} from '@util/LocaleUtils';
import {SCHEMA_VERSION} from '../../../../api/schema.version';
import {CachePersistor} from 'apollo-cache-persist';
import localForage from 'localforage';
import deepEqual from 'deep-equal';
import pathOr from 'ramda/src/pathOr';

const USER_META = pathOr( {}, ['upi', 'User', 'info'], window );
const CACHE_META_KEY = 'apollo-cache-meta';
export const MAX_PERSISTED_CACHE_SIZE = 10 * 1024 * 1024;
const CACHE_META = {
    ...USER_META,
    locale: getSiteLocale(),
    schemaVersion: SCHEMA_VERSION
};

let activePersistor = null;
let quotaErrorHandler = null;

export function createBoundedStorage ( storage, maxSize ) {
    return {
        getItem: key => storage.getItem( key ),
        removeItem: key => storage.removeItem( key ),
        setItem: async ( key, value ) => {
            if ( typeof value === 'string' && value.length > maxSize ) {
                await storage.removeItem( key );
                return null;
            }

            return storage.setItem( key, value );
        }
    };
}

const boundedStorage = createBoundedStorage( localForage, MAX_PERSISTED_CACHE_SIZE );

const StorageProvider = {
    init: async cache => {
        await StorageProvider.teardown();

        try {
            const serializedCacheMeta = await localForage.getItem( CACHE_META_KEY );
            const cacheMeta = serializedCacheMeta ? JSON.parse( serializedCacheMeta ) : null;
            const persistor = new CachePersistor( {
                cache,
                storage: boundedStorage,
                debounce: 200,
                maxSize: MAX_PERSISTED_CACHE_SIZE
            } );
            activePersistor = persistor;

            quotaErrorHandler = function ( e ) {
                if ( e.error && e.error.name === 'QuotaExceededError' ) {
                    persistor.purge();
                }
            };
            window.addEventListener( 'error', quotaErrorHandler );

            if ( deepEqual( cacheMeta, CACHE_META ) ) {
                await persistor.restore();
            }
            else {
                await persistor.purge();
                console.warn( '[ApolloClient] Cache purged... version:', SCHEMA_VERSION );
                await localForage.setItem( CACHE_META_KEY, JSON.stringify( CACHE_META ) );
            }

            return persistor;
        }
        catch ( err ) {
            await StorageProvider.teardown();
            console.warn( '[Apollo CachePersistor] Persistor disabled - localForage not available' );
            return null;
        }
    },

    reset: async client => {
        const persistor = activePersistor;

        if ( persistor ) {
            persistor.pause();
            await persistor.purge();
        }

        try {
            await client.resetStore();
        }
        finally {
            if ( persistor && persistor === activePersistor ) {
                persistor.resume();
                await persistor.persist();
            }
        }
    },

    teardown: async () => {
        if ( quotaErrorHandler ) {
            window.removeEventListener( 'error', quotaErrorHandler );
            quotaErrorHandler = null;
        }

        if ( activePersistor ) {
            activePersistor.pause();
            activePersistor.remove();
            activePersistor = null;
        }
    }
};

export default StorageProvider;
